import * as Yup from 'yup';
import { SIMPLE_EMAIL_REGEX } from '../../helpers/constants';

const commaSeparatedEmails = (errorMessage) =>
    Yup.string()
        .nullable()
        .test('comma-separated-email-list', errorMessage, function validate(value) {
            if (!value || !value.trim()) return true;
            const emails = value
                .split(',')
                .map((e) => e.trim())
                .filter(Boolean);
            if (emails.length === 0) {
                return this.createError({ message: errorMessage });
            }
            const bad = emails.find((email) => !SIMPLE_EMAIL_REGEX.test(email));
            if (bad) {
                return this.createError({ message: `${errorMessage}: ${bad}` });
            }
            return true;
        });

export const createShowValidationSchema = (t) =>
    Yup.object().shape({
        title: Yup.string().required(t('common.required')),
        description: Yup.mixed().test(
            'is-string-or-object',
            'Invalid type',
            (val) =>
                val === null ||
                typeof val === 'string' ||
                (typeof val === 'object' && val.type === 'doc'),
        ),
        cancelWindow: Yup.number(),
        cancelDescription: Yup.mixed().test(
            'is-string-or-object',
            'Invalid type',
            (val) =>
                val === null ||
                typeof val === 'string' ||
                (typeof val === 'object' && val.type === 'doc'),
        ),
        needToKnow: Yup.mixed().test(
            'is-string-or-object',
            'Invalid type',
            (val) =>
                val === null ||
                typeof val === 'string' ||
                (typeof val === 'object' && val.type === 'doc'),
        ),
        location: Yup.string().url(t('common.invalidUrl')).nullable(),
        venueId: Yup.number().required(t('common.required')),
        isIcalEnabled: Yup.number().oneOf([0, 1]),
        icalUrl: Yup.string().when('isIcalEnabled', {
            is: 1,
            then: (schema) => schema.required(t('common.required')).url(t('common.invalidUrl')),
            otherwise: (schema) => schema.nullable(),
        }),
        autoPublishDaysInAdvance: Yup.number()
            .min(0)
            .when('isIcalEnabled', {
                is: 1,
                then: (schema) => schema.required(t('common.required')),
                otherwise: (schema) => schema.nullable(),
            }),
        reviewText: Yup.mixed().test(
            'is-string-or-object',
            'Invalid type',
            (val) =>
                val === null ||
                typeof val === 'string' ||
                (typeof val === 'object' && val.type === 'doc'),
        ),
        reviewLink: Yup.string().nullable().url(t('common.invalidUrl')),
        enabledAbandonedCartEmail: Yup.boolean(),
        reservationTime: Yup.number()
            .min(5, t('shows.validation.reservationTimeMin'))
            .required(t('common.required')),
        rollbackTimeSize: Yup.number()
            .min(5, t('shows.validation.rollbackTimeSize'))
            .required(t('common.required')),
        bookingWindow: Yup.number().min(0).nullable().optional(),
        tripadvisorUrl: Yup.string().nullable().url(t('common.invalidUrl')),
        isBreakingOutFees: Yup.number().oneOf([0, 1, 2]),
        priceListingMode: Yup.string().oneOf(['gross', 'net']),
        fees: Yup.array().of(
            Yup.object()
                .shape({
                    name: Yup.string().required(t('common.required')),
                    percentage: Yup.number()
                        .min(0, t('common.positiveNumber'))
                        .max(100, t('common.maxPercentage'))
                        .nullable(),
                    usd: Yup.number().min(0).nullable(),
                })
                .test('exactly-one-percentage-or-usd', t('shows.feesValidation'), function (fee) {
                    const { path } = this;
                    const index = path.match(/\d+/) ? Number(path.match(/\d+/)[0]) : null;
                    const hasPercentage =
                        fee.percentage !== null &&
                        fee.percentage !== '' &&
                        Number(fee.percentage) > 0;
                    const hasUsd = fee.usd !== null && fee.usd !== '' && Number(fee.usd) > 0;
                    if ((hasPercentage && hasUsd) || (!hasPercentage && !hasUsd)) {
                        return this.createError({
                            path,
                            message: `${t('shows.feesValidation')} (Fee #${index !== null ? index + 1 : ''})`,
                        });
                    }
                    return true;
                }),
        ),
        marketing: Yup.object().shape({
            email: Yup.string().email(t('common.invalidEmail')).required(t('common.required')),
            // if emailI18n is provided, every value should be email format
            emailI18n: Yup.mixed().test('are-emails-valid', t('common.invalidEmail'), (val) => {
                if (val && typeof val === 'object') {
                    return Object.values(val).every(
                        (email) =>
                            email === '' ||
                            (typeof email === 'string' && SIMPLE_EMAIL_REGEX.test(email)),
                    );
                }
                return true;
            }),
        }),
        waitlistNotificationEmails: commaSeparatedEmails(
            t('shows.columns.waitlistNotificationEmailsInvalid'),
        ),
        checkinNotificationEmails: commaSeparatedEmails(
            t('shows.columns.checkinNotificationEmailsInvalid'),
        ),
        upgradeNotificationEmails: commaSeparatedEmails(
            t('shows.columns.upgradeNotificationEmailsInvalid'),
        ),
    });
